import { useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Clip, ClipType } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCreateDuplicateClipTask } from '~/components/Modals/DuplicateClip/hooks/useCreateDuplicateClipTask';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';

export type DuplicateClipModalProps = {
  clip: Clip;
  targetBoardId?: string;
  trackLocation: AirActionTrackingLocation;
};

export const DuplicateClipModal = ({
  onClose,
  clip,
  targetBoardId,
  trackLocation,
}: AirModalProps<DuplicateClipModalProps>) => {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const { showToast } = useToasts();
  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { createDuplicateClipTask } = useCreateDuplicateClipTask();

  const isFile = clip.type === ClipType.nonMedia;
  const itemLabel = isFile ? 'file' : 'asset';
  const itemLabelWithArticle = isFile ? 'a file' : 'an asset';

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  const duplicate = async () => {
    try {
      setIsDuplicating(true);
      await createDuplicateClipTask({
        sourceAssetId: clip.assetId,
        sourceItemTitle: clip.title || clip.importedName || 'Untitled',
        targetBoardId,
        isFile,
        trackLocation,
      });
      setIsDuplicating(false);
      onClose();
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Failed to duplicate clip',
        metadata: {
          assetId: clip.assetId,
          clipId: clip.id,
          trackLocation,
          targetBoardId,
        },
      });

      setIsDuplicating(false);
      showToast(`There was an error while trying to duplicate this ${itemLabel}. Please try again later.`);
    }
  };

  return (
    <ConfirmationModal
      id="duplicate-clip-modal"
      isOpen
      title={`Duplicate ${itemLabel}`}
      ctaPrimaryText="Duplicate"
      isLoading={isDuplicating}
      isDisabled={isDuplicating}
      onConfirm={duplicate}
      data-testid="DUPLICATE_CLIP_MODAL"
      description={`Duplicating ${itemLabelWithArticle} will make a copy in this workspace, including its versions and metadata.`}
      onDismiss={onClose}
    />
  );
};
